import { useMemo, useRef } from "react"
import DropdownTrigger from "./dropdown-trigger"
import useOutsideClick from "@/hooks/use-outside-click"
import { useOverlayStore } from "@/hooks/use-overlay-store"
import { langStore } from "@/stores/lang-store"
import { dropdownBundle } from "./dropdown-const"
import { RESOURCE_COLOR_MAP } from "@/constants/resource-const"

const DropdownHo = ({
  value,
  options = [],
  onChange,
  placeholder = "호 선택",
  prevent = null,
  disabled = false,
  currentResource = "water"
}) => {
  const { lang } = langStore()
  const wrapperRef = useRef(null)
  const { activeId, toggle, close } = useOverlayStore()

  const selected = useMemo(() => {
    return options.find((option) => +option.id === +value)
  }, [options, value])

  const groupedOptions = useMemo(() => {
    return options.reduce((acc, current) => {
      const textFloor = String(current.id).padStart(4, "0")
      const floor = textFloor.slice(0, -2) || textFloor.slice(0, 2)
      if (!acc[floor]) acc[floor] = []
      acc[floor].push(current)
      return acc
    }, {})
  }, [options])

  const preventDropdown = () => {
    if (disabled && prevent) return prevent()
    if (disabled) return
    toggle("ho")
  }

  const handleSelect = (id) => {
    onChange?.(id)
    close()
  }

  useOutsideClick({
    ref: wrapperRef,
    enabled: activeId === "ho",
    onOutside: () => close()
  })

  return (
    <div ref={wrapperRef} className="relative">
      <DropdownTrigger
        label={selected?.label}
        placeholder={placeholder}
        targetId={"ho"}
        onClick={preventDropdown}
      />

      {activeId === "ho" && (
        <div className="absolute right-0 md:left-0 z-30 mt-2 min-w-[calc(200%+8px)] md:min-w-100 rounded-2xl border border-gray-200 bg-white p-4 pr-2 shadow-xl">
          <div className="max-h-100 pr-2 overflow-y-auto space-y-5">
            {Object.entries(groupedOptions)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([floor, rooms]) => (
                <section key={floor}>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="whitespace-nowrap text-xs font-bold text-gray-400">
                      {floor}
                      {dropdownBundle.floor.suffix[lang]}
                    </span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {rooms.map((room) => {
                      const active = room.id === value

                      const filteredBadges = (room.badges || []).filter((badgeKey) => {
                        if (currentResource === "water") {
                          return badgeKey === "leak" || badgeKey === "water"
                        }
                        return badgeKey === currentResource
                      })

                      return (
                        <button
                          key={room.id}
                          type="button"
                          onClick={() => handleSelect(room.id)}
                          className={`
                            relative rounded-lg border px-2 py-2
                            text-sm font-medium transition-all duration-150 cursor-pointer
                            ${
                              active
                                ? "border-indigo-500 bg-indigo-500 text-white shadow-md scale-[1.02]"
                                : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"
                            }
                          `}
                        >
                          {!!filteredBadges.length && (
                            <div className="absolute -top-1 left-1 flex items-center">
                              {filteredBadges.map((badge, idx) => (
                                <div
                                  key={`${room.id}-${badge}`}
                                  className="w-2.5 h-2.5 rounded-full border border-white shadow-sm"
                                  style={{
                                    backgroundColor: RESOURCE_COLOR_MAP[badge] || "#9ca3af",
                                    marginLeft: idx === 0 ? 0 : -2
                                  }}
                                />
                              ))}
                            </div>
                          )}

                          {room.label}
                        </button>
                      )
                    })}
                  </div>
                </section>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DropdownHo
